import { getPathMatch } from "next/dist/shared/lib/router/utils/path-match";

// Routes this app serves under its [locale] segment. A route may only appear
// here once its page physically lives under app/[locale]/… — resolveLocaleRoute
// rewrites a localizable unprefixed path to /en/…, so listing a route with no
// [locale] page would rewrite it to a 404. Unprefixed URLs for these routes
// rewrite to the default locale; prefixed URLs redirect to canonical until the
// locale launches (see LAUNCHED).
//
// LAUNCHED is a further gate on top of LOCALIZABLE, and it is per locale: a
// route can be served under [locale] (e.g. for /ps QA on preview) before its
// translations ship, and translations land one locale at a time.
export const LOCALIZABLE_ROUTES: ReadonlySet<string> = new Set<string>([
  "/",
  "/6sense-personalisation/experiment",
  "/ai-instructions",
  "/auth",
  "/why-choose-intercom",
  "/contact-sales",
  "/customer-transformation-report",
  "/dsa-report-form",
  "/early-stage",
  "/enterprise",
  "/helpdesk",
  "/intercom-link",
  "/intercom-link/support",
  "/pricing",
  "/pricing/calculator",
  "/privacy-form",
  "/privacy-form/thank-you",
  "/security",
  "/solution-partner-program",
  "/solutions/ecommerce",
  "/solutions/fintech",
  "/solutions/gaming",
  "/solutions/technology",
  "/technology-partner-program",
  "/thank-you",
  "/view-demos",
  "/ai-chatbot",
  "/compare-intercom-vs-zendesk",
  "/conversational-ai",
  "/live-chat",
  "/small-business",
  "/startups-program",
  "/support-for-agents/phone",
  "/helpdesk/copilot",
  "/helpdesk/help-center",
  "/helpdesk/inbox",
  "/helpdesk/knowledge-hub",
  "/helpdesk/omnichannel",
  "/helpdesk/outbound",
  "/helpdesk/reporting",
  "/helpdesk/tickets",
]);
// Each launched route maps to the locale identifiers it is live in, so a route
// can ship in French while Japanese is still being translated. A locale absent
// here is never served under its prefix, linked to, or advertised as an
// hreflang alternate — which is the point: claiming a language we render in
// English is worse for search than not claiming it.
// Not read from localeConfig: next.config pulls this module in via markdown-rewrites.ts, and its isolated transpiler cannot resolve that alias.
// ja-JP is tier 1 but held back; it serves from its interim dir instead.
const TIER_1_LOCALES: ReadonlySet<string> = new Set([
  "fr-FR",
  "es-419",
  "de-DE",
  "pt-BR",
]);

export const LAUNCHED_LOCALIZED_ROUTES: ReadonlyMap<
  string,
  ReadonlySet<string>
> = new Map([
  ["/", TIER_1_LOCALES],
  ["/contact-sales", TIER_1_LOCALES],
  ["/helpdesk", TIER_1_LOCALES],
  ["/pricing", TIER_1_LOCALES],
  ["/view-demos", TIER_1_LOCALES],
]);

// Serves nothing translated; it only orders the page's strings and assets ahead of launch.
export const UPCOMING_LOCALIZED_ROUTES: ReadonlySet<string> = new Set([
  "/early-stage",
  "/helpdesk/omnichannel",
  "/intercom-link",
  "/pricing/calculator",
]);

// Whole trees served under [locale]. Their pages are content-driven ([slug],
// [...slug]), so the set of routes is not knowable here — a prefix covers the
// index and every descendant without enumerating slugs.
export const LOCALIZABLE_PREFIXES: readonly string[] = [
  "/blog",
  "/customers",
  "/drlp",
  "/integrations",
  "/legal",
  "/learning-center",
  "/legacy-drlp",
];
export const LAUNCHED_LOCALIZED_PREFIXES: ReadonlyMap<
  string,
  ReadonlySet<string>
> = new Map();

// Matching is at a segment boundary, so "/customers-list" is not caught by
// "/customers" — a raw startsWith would rewrite unrelated sibling routes into
// [locale], where they have no page and would 404.
function matchesPrefix(route: string, prefixes: readonly string[]): boolean {
  return prefixes.some(
    (prefix) => route === prefix || route.startsWith(`${prefix}/`),
  );
}

export function isLocalizableRoute(route: string): boolean {
  if (LOCALIZABLE_ROUTES.has(route)) return true;
  return matchesPrefix(route, LOCALIZABLE_PREFIXES);
}

export function isLaunchedLocalizedRoute(
  route: string,
  locale: string,
): boolean {
  if (LAUNCHED_LOCALIZED_ROUTES.get(route)?.has(locale)) return true;
  for (const [prefix, locales] of LAUNCHED_LOCALIZED_PREFIXES) {
    if (matchesPrefix(route, [prefix]) && locales.has(locale)) return true;
  }
  return false;
}

export interface LaunchedRoute {
  route: string;
  /**
   * A tree launch covers `route` and every page below it; an exact launch
   * covers that one page. Callers that reason about a route's content — its
   * catalog entries, its URLs — must not conflate the two.
   */
  tree: boolean;
}

/**
 * The launch tables inverted, for callers that enumerate a locale's pages
 * rather than ask about one route.
 */
export function launchedRoutesByLocale(): ReadonlyMap<
  string,
  readonly LaunchedRoute[]
> {
  const byLocale = new Map<string, LaunchedRoute[]>();
  for (const [table, tree] of [
    [LAUNCHED_LOCALIZED_ROUTES, false],
    [LAUNCHED_LOCALIZED_PREFIXES, true],
  ] as const) {
    for (const [route, locales] of table) {
      for (const locale of locales) {
        const routes = byLocale.get(locale) ?? [];
        routes.push({ route, tree });
        byLocale.set(locale, routes);
      }
    }
  }
  return byLocale;
}

/**
 * Not read from localeConfig: next.config.ts pulls this module in (via
 * markdown-rewrites), and its isolated TS transpiler cannot resolve the
 * "@marketing-sites/l10n" alias that localeConfig imports. The locale each
 * prefix serves is spelled out alongside it because the launch gate is keyed by
 * locale, not prefix. The pseudo prefix is excluded — it is pre-rendered from
 * `isPreview`, and never gets a markdown mirror (see
 * generate-markdown-pages.mjs). Tests assert these stay equal to their
 * localeConfig counterparts.
 */
export const DEFAULT_LOCALE_PREFIX = "en";
export const REAL_LOCALES: readonly (readonly [
  prefix: string,
  locale: string,
])[] = [
  ["fr-fr", "fr-FR"],
  ["es-la", "es-419"],
  ["de-de", "de-DE"],
  ["pt-br", "pt-BR"],
];
export const REAL_LOCALE_PREFIXES: readonly string[] = REAL_LOCALES.map(
  ([prefix]) => prefix,
);

// Build-time guard. next.config redirects resolve BEFORE middleware, so a
// localizable route that is also a redirect source diverges by locale: the
// redirect wins at the canonical URL (the [locale] page never serves) while a
// prefixed URL misses the redirect, rewrites into [locale], and keeps serving
// the retired page.
export function assertLocalizableRoutesNotRedirected(
  redirectSources: Iterable<string>,
  {
    localizableRoutes = LOCALIZABLE_ROUTES,
    localizablePrefixes = LOCALIZABLE_PREFIXES,
    launchedPrefixes = LAUNCHED_LOCALIZED_PREFIXES.keys(),
  }: {
    localizableRoutes?: ReadonlySet<string>;
    localizablePrefixes?: readonly string[];
    launchedPrefixes?: Iterable<string>;
  } = {},
): void {
  const launched = [...launchedPrefixes];
  // A tree's index is a concrete route and conflicts like any other. Its
  // descendants are not: retired slugs legitimately redirect while the tree
  // stays localizable, so they only diverge once that tree's locale launches.
  const routes = [...localizableRoutes, ...localizablePrefixes];
  const conflicts = new Set<string>();
  const launchedDescendants = new Set<string>();

  for (const source of redirectSources) {
    // Matched with Next's own route matcher so the guard agrees with how Next
    // resolves redirects: a pattern source like "/foo/:path*" also matches bare
    // "/foo", which a literal compare would miss entirely. Sources the matcher
    // cannot parse fall back to literal equality.
    let matches: (route: string) => boolean;
    try {
      const matcher = getPathMatch(source);
      matches = (route) => matcher(route) !== false;
    } catch {
      matches = (route) => route === source;
    }
    for (const route of routes) {
      if (matches(route)) conflicts.add(route);
    }
    if (matchesPrefix(source, launched)) launchedDescendants.add(source);
  }

  if (conflicts.size > 0) {
    throw new Error(
      `[l10n intercom-com] Localizable routes are also next.config redirect ` +
        `sources: ${[...conflicts].join(", ")}. Redirects run before middleware, so the ` +
        `canonical URL would redirect while its locale-prefixed variants keep ` +
        `serving the page. Remove the redirect source, or drop the route from ` +
        `LOCALIZABLE_ROUTES / LOCALIZABLE_PREFIXES and delete its app/[locale] page.`,
    );
  }

  if (launchedDescendants.size > 0) {
    throw new Error(
      `[l10n intercom-com] next.config redirect sources fall under a launched ` +
        `localizable prefix: ${[...launchedDescendants].join(", ")}. The unprefixed ` +
        `URL redirects before middleware runs, but the launched prefixed variant ` +
        `rewrites into [locale] and serves the retired page. Add locale-prefixed ` +
        `sources for these redirects, or unlaunch the prefix.`,
    );
  }
}

// LAUNCHED is a gate on top of LOCALIZABLE, never a superset: a launched route
// that isn't localizable passes through middleware untouched and 404s in every
// locale, since its page only exists under [locale].
export function assertLaunchedRoutesAreLocalizable(): void {
  const orphans = [
    ...LAUNCHED_LOCALIZED_ROUTES.keys(),
    ...LAUNCHED_LOCALIZED_PREFIXES.keys(),
  ].filter((route) => !isLocalizableRoute(route));
  if (orphans.length === 0) return;

  throw new Error(
    `[l10n intercom-com] Launched entries are not localizable: ` +
      `${orphans.join(", ")}. A launched route or prefix must first appear in ` +
      `LOCALIZABLE_ROUTES / LOCALIZABLE_PREFIXES, or the locale router never ` +
      `rewrites it into [locale].`,
  );
}
